// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

const routes: (RouteRecordRaw & {
    meta?: {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;
    };
})[] = [
    /* General / Home */

    {
        name: "home",
        path: "/",
        component: () => import("@/components/routes/HomePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    /* Auth */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
    },

    {
        name: "tfa-login",
        path: "/login/tfa",
        component: () => import("@/components/routes/auth/TwoFactorLoginPage.vue"),
    },

    {
        name: "signup",
        path: "/signup",
        component: () => import("@/components/routes/auth/SignupPage.vue"),
    },
    {
        name: "signup-success",
        path: "/signup/success",
        component: () => import("@/components/routes/auth/SignupSuccessPage.vue"),
    },

    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
    },

    {
        name: "tp-login",
        path: "/login/tp/:service",
        component: () => import("@/components/routes/auth/ThirdPartyLogin.vue"),
    },

    {
        name: "tp-signup",
        path: "/signup/tp",
        component: () => import("@/components/routes/auth/ThirdPartySignupPage.vue"),
    },

    /* Profile */

    {
        name: "profile",
        path: "/user/:username",
        component: () => import("@/components/routes/profile/ProfilePage.vue"),
    },

    /* Account */

    {
        name: "account-settings",
        path: "/account-settings",
        component: () => import("@/components/routes/account/AccountSettingsPage.vue"),
    },

    /* Admin */

    {
        name: "admin",
        path: "/admin",
        component: () => import("@/components/routes/admin/AdministrationPage.vue"),
    },

    {
        name: "admin-user",
        path: "/admin/users/:id",
        component: () => import("@/components/routes/admin/UserPage.vue"),
    },

    /* Data space configuration */

    {
        name: "admin-data-space",
        path: "/admin/data-space",
        component: () => import("@/components/routes/admin/DataSpaceConfigurationPage.vue"),
    },

    /* Data sources */

    {
        name: "data-sources",
        path: "/data-sources",
        component: () => import("@/components/routes/sources/DataSourcesListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "data-sources-create",
        path: "/data-sources-create",
        component: () => import("@/components/routes/sources/CreateDataSourcePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "data-source",
        path: "/data-sources/:id",
        component: () => import("@/components/routes/sources/DataSourcePage.vue"),
    },

    /* Data sinks */

    {
        name: "data-sinks",
        path: "/data-sinks",
        component: () => import("@/components/routes/sinks/DataSinksListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "data-sinks-create",
        path: "/data-sinks-create",
        component: () => import("@/components/routes/sinks/CreateDataSinkPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "data-sink",
        path: "/data-sinks/:id",
        component: () => import("@/components/routes/sinks/DataSinkPage.vue"),
    },

    /* Data pipelines */

    {
        name: "pipelines",
        path: "/pipelines",
        component: () => import("@/components/routes/pipelines/PipelineListPage.vue"),
        meta: {
            sidebarSticky: false,
        },
    },

    {
        name: "pipeline-create",
        path: "/pipelines-create",
        component: () => import("@/components/routes/pipelines/CreatePipelinePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "pipeline",
        path: "/pipelines/:id",
        component: () => import("@/components/routes/pipelines/PipelinePage.vue"),
    },

    {
        name: "execution",
        path: "/pipelines/:pid/executions/:id",
        component: () => import("@/components/routes/pipelines/ExecutionPage.vue"),
    },

    {
        name: "analysis-result",
        path: "/pipelines/:pid/analysis-results/:id",
        component: () => import("@/components/routes/pipelines/AnalysisResultPage.vue"),
    },

    /* Catalogs */

    {
        name: "catalogs",
        path: "/catalogs",
        component: () => import("@/components/routes/catalog/CatalogListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "catalog-create",
        path: "/catalog-create",
        component: () => import("@/components/routes/catalog/CreateCatalogPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "catalog",
        path: "/catalogs/:id",
        component: () => import("@/components/routes/catalog/CatalogPage.vue"),
    },

    /* Datasets */

    {
        name: "datasets",
        path: "/datasets",
        component: () => import("@/components/routes/dataset/DatasetListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "dataset-create",
        path: "/dataset-create",
        component: () => import("@/components/routes/dataset/CreateDatasetPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "dataset",
        path: "/dataset/:id",
        component: () => import("@/components/routes/dataset/DatasetPage.vue"),
    },

    /* Negotiations */

    {
        name: "negotiations",
        path: "/negotiations",
        component: () => import("@/components/routes/negotiation/NegotiationListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "negotiation-create",
        path: "/negotiation-create",
        component: () => import("@/components/routes/negotiation/CreateNegotiationPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "negotiation",
        path: "/negotiation/:id",
        component: () => import("@/components/routes/negotiation/NegotiationPage.vue"),
    },

    /* Agreements */

    {
        name: "agreements",
        path: "/agreements",
        component: () => import("@/components/routes/agreement/AgreementsListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "agreement",
        path: "/agreement/:id",
        component: () => import("@/components/routes/agreement/AgreementPage.vue"),
    },

    /* Transfers */

    {
        name: "transfers",
        path: "/transfers",
        component: () => import("@/components/routes/transfer/TransferListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "transfer-create",
        path: "/transfer-create",
        component: () => import("@/components/routes/transfer/CreateTransferPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "transfer",
        path: "/transfer/:id",
        component: () => import("@/components/routes/transfer/TransferPage.vue"),
    },

    /* Notifications */

    {
        name: "notifications",
        path: "/notifications",
        component: () => import("@/components/routes/notifications/NotificationsPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    /* Wallet */

    {
        name: "admin-wallet",
        path: "/admin/wallet/:id",
        component: () => import("@/components/routes/admin/wallet/WalletSettingsPage.vue"),
    },

    /* Clearing house */

    {
        name: "clearing-house",
        path: "/clearing-house",
        component: () => import("@/components/routes/clearing-house/ClearingHousePage.vue"),
    },

    {
        name: "clearing-house-agreement",
        path: "/clearing-house/agreements/:id",
        component: () => import("@/components/routes/clearing-house/RegisteredAgreementPage.vue"),
    },

    /* Broker requests */

    // {
    //     name: "broker-request",
    //     path: "/admin/broker-requests/:id",
    //     component: () => import("@/components/routes/admin/broker-requests/BrokerRequestPage.vue"),
    // },

    // {
    //     name: "provider-request",
    //     path: "/catalog-broker-requests/received/:id",
    //     component: () => import("@/components/routes/broker-requests/received/ProviderRequestPage.vue"),
    // },

    /* Default */

    {
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
    },
];

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
